import React, { useEffect, useMemo, useRef, useState } from "react";
import { Box, Typography } from "@mui/material";
import { ResponsivePie } from "@nivo/pie";

const COLORS = {
  Answered: "#2e7d32",
  Missed: "#f59e0b",
  Failed: "#dc2626",
};

// Lighter highlight tone per slice — used for the radial gradient origin so
// each slice has a "lit-from-the-top" sheen, the visual cue that sells depth.
const HIGHLIGHTS = {
  Answered: "#86efac",
  Missed: "#fde68a",
  Failed: "#fca5a5",
};

// CSS keyframes injected once per page. Defined at module scope so
// re-renders don't recreate the <style> tag.
const STYLE_TAG_ID = "call-distribution-pie-3d-keyframes";
function ensureKeyframes() {
  if (typeof document === "undefined") return;
  if (document.getElementById(STYLE_TAG_ID)) return;
  const style = document.createElement("style");
  style.id = STYLE_TAG_ID;
  style.textContent = `
    @keyframes cdpEnter {
      0%   { transform: perspective(1000px) rotateX(55deg) rotateZ(-180deg) scale(0.6); opacity: 0; }
      60%  { opacity: 1; }
      100% { transform: perspective(1000px) rotateX(55deg) rotateZ(0deg) scale(1); opacity: 1; }
    }
    @keyframes cdpFloat {
      0%, 100% { transform: perspective(1000px) rotateX(55deg) translateY(0); }
      50%      { transform: perspective(1000px) rotateX(55deg) translateY(-4px); }
    }
  `;
  document.head.appendChild(style);
}

// Smooth count-up for the center total. Quadratic ease-out so it lands
// crisply rather than crawling at the end.
function useCountUp(target, durationMs = 900) {
  const [val, setVal] = useState(0);
  const rafRef = useRef(null);
  useEffect(() => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    const start = performance.now();
    const from = 0;
    const tick = (now) => {
      const t = Math.min(1, (now - start) / durationMs);
      const eased = 1 - (1 - t) * (1 - t);
      setVal(Math.round(from + (target - from) * eased));
      if (t < 1) rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
  }, [target, durationMs]);
  return val;
}

function CallDistributionPie({ answered = 0, missed = 0, failed = 0, isDark = false }) {
  ensureKeyframes();

  const data = useMemo(() => {
    const items = [];
    if (answered > 0) items.push({ id: "Answered", label: "Answered", value: answered });
    if (missed > 0)   items.push({ id: "Missed",   label: "Missed",   value: missed });
    if (failed > 0)   items.push({ id: "Failed",   label: "Failed",   value: failed });
    return items;
  }, [answered, missed, failed]);

  const total = answered + missed + failed;
  const animatedTotal = useCountUp(total);

  if (total === 0) {
    return (
      <Box sx={{ textAlign: "center", py: 4 }}>
        <Typography variant="body2" sx={{ color: isDark ? "#8899aa" : "#999" }}>
          No call data available for today
        </Typography>
      </Box>
    );
  }

  const textColor = isDark ? "#e0e0e0" : "#333";
  const legendColor = isDark ? "#b0bec5" : "#555";

  // Radial-gradient defs — one per slice id. The "rule" matchers below tell
  // Nivo which slice gets which gradient.
  const gradientDefs = data.map((d) => ({
    id: `grad-${d.id}`,
    type: "radialGradient",
    colors: [
      { offset: 0, color: HIGHLIGHTS[d.id] || COLORS[d.id] },
      { offset: 100, color: COLORS[d.id] },
    ],
  }));
  const gradientFills = data.map((d) => ({
    match: { id: d.id },
    id: `grad-${d.id}`,
  }));

  return (
    <Box sx={{ height: "380px", display: "flex", flexDirection: "column" }}>
      {/* Chart area (donut + center label) — fills the space above the legend strip. */}
      <Box sx={{ flex: 1, position: "relative", overflow: "visible" }}>
        {/* Tilted disc — perspective + rotateX gives true 3D look on a 2D donut.
            Math stays accurate (the SVG is rendered flat, then the whole element
            is tilted as a unit, so slice angles are preserved — no foreshortening
            distortion of the underlying data). */}
        <Box
          sx={{
            height: "100%",
            width: "100%",
            transformStyle: "preserve-3d",
            transformOrigin: "center 60%",
            animation: "cdpEnter 1.1s cubic-bezier(0.22, 1, 0.36, 1) both, cdpFloat 5s ease-in-out 1.2s infinite",
            filter: "drop-shadow(0 18px 18px rgba(0,0,0,0.28))",
            willChange: "transform",
          }}
        >
          <ResponsivePie
            data={data}
            margin={{ top: 10, right: 20, bottom: 20, left: 20 }}
            innerRadius={0.68}
            padAngle={2}
            cornerRadius={4}
            activeOuterRadiusOffset={18}
            colors={(d) => COLORS[d.id]}
            borderWidth={1}
            borderColor={{ from: "color", modifiers: [["darker", 0.3]] }}
            // Outside link-labels off — they look noisy on a tilted disc.
            // Built-in legends off — replaced with a custom legend strip below
            // (more readable: shows color dot + label + count + percentage).
            enableArcLinkLabels={false}
            arcLabelsSkipAngle={10}
            arcLabelsTextColor="#fff"
            arcLabel={(d) => `${Math.round((d.value / total) * 100)}%`}
            // Bigger, bolder in-slice % labels so they stay readable at any
            // tilt; tooltip text below is bumped up too.
            theme={{
              labels: {
                text: {
                  fontSize: 15,
                  fontWeight: 700,
                  textShadow: "0 1px 2px rgba(0,0,0,0.35)",
                },
              },
            }}
            // Radial gradient fills add a "lit from above" highlight — this is
            // what really sells the 3D look once the disc is tilted.
            defs={gradientDefs}
            fill={gradientFills}
            tooltip={({ datum }) => {
              const pct = Math.round((datum.value / total) * 100);
              const sliceColor = COLORS[datum.id];
              return (
                <Box
                  sx={{
                    background: isDark ? "#1a2332" : "#ffffff",
                    color: isDark ? "#e0e0e0" : "#1f2937",
                    px: 2,
                    py: 1.4,
                    borderRadius: 1.5,
                    border: isDark
                      ? "1px solid #2a3a4a"
                      : "1px solid #e5e7eb",
                    boxShadow: `0 10px 28px ${sliceColor}55, 0 2px 8px rgba(0,0,0,0.18)`,
                    minWidth: 170,
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      mb: 0.6,
                    }}
                  >
                    <Box
                      sx={{
                        width: 12,
                        height: 12,
                        borderRadius: "50%",
                        background: `linear-gradient(135deg, ${
                          HIGHLIGHTS[datum.id] || sliceColor
                        } 0%, ${sliceColor} 100%)`,
                        border: `1px solid ${sliceColor}`,
                        flexShrink: 0,
                      }}
                    />
                    <Typography
                      sx={{
                        fontSize: "1.05rem",
                        fontWeight: 700,
                        color: sliceColor,
                        lineHeight: 1.1,
                      }}
                    >
                      {datum.id}
                    </Typography>
                  </Box>
                  <Typography
                    sx={{
                      fontSize: "0.95rem",
                      fontWeight: 600,
                      color: isDark ? "#e0e0e0" : "#1f2937",
                      lineHeight: 1.3,
                    }}
                  >
                    {datum.value} call{datum.value === 1 ? "" : "s"} · {pct}%
                  </Typography>
                </Box>
              );
            }}
            legends={[]}
            motionConfig={{ mass: 1, tension: 120, friction: 18 }}
          />
        </Box>

        {/* Center label — sibling overlay (NOT inside the tilted Box) so the
            number stays upright and readable while the disc is tilted. */}
        <Box
          sx={{
            position: "absolute",
            top: "44%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            textAlign: "center",
            pointerEvents: "none",
            zIndex: 2,
          }}
        >
          <Typography sx={{ fontSize: "1.7rem", fontWeight: 700, color: textColor, lineHeight: 1.1 }}>
            {animatedTotal}
          </Typography>
          <Typography sx={{ fontSize: "0.72rem", color: legendColor, fontWeight: 500 }}>
            Total Calls
          </Typography>
        </Box>
      </Box>

      {/* Custom legend strip — clearer than Nivo's default: color dot + label
          + raw count + percentage on one line per slice, all readable at once. */}
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          justifyContent: "center",
          gap: { xs: 1.5, sm: 2.5, md: 3.5 },
          pt: 1.5,
          pb: 0.5,
        }}
      >
        {data.map((d) => {
          const pct = Math.round((d.value / total) * 100);
          return (
            <Box
              key={d.id}
              sx={{ display: "flex", alignItems: "center", gap: 0.8 }}
            >
              <Box
                sx={{
                  width: 12,
                  height: 12,
                  borderRadius: "50%",
                  background: `linear-gradient(135deg, ${HIGHLIGHTS[d.id] || COLORS[d.id]} 0%, ${COLORS[d.id]} 100%)`,
                  border: `1px solid ${COLORS[d.id]}`,
                  flexShrink: 0,
                  boxShadow: `0 0 6px ${COLORS[d.id]}55`,
                }}
              />
              <Typography
                sx={{ fontSize: "0.85rem", fontWeight: 700, color: textColor }}
              >
                {d.label}
              </Typography>
              <Typography
                sx={{ fontSize: "0.8rem", color: legendColor, fontWeight: 500 }}
              >
                {d.value} · {pct}%
              </Typography>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}

export default CallDistributionPie;
